import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, X, ChevronLeft, ChevronRight, Film, Image, Shuffle } from 'lucide-react';
import { getShows } from '../services/cmsData';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://api.mt-productions.co.uk';
const PUBLIC_URL = process.env.PUBLIC_URL || '';

// Gallery Image with skeleton loading
const GalleryImage = ({ image, index, onClick }) => {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ delay: (index % 8) * 0.03 }}
      className="break-inside-avoid cursor-pointer group relative"
      onClick={onClick}
    >
      {/* Skeleton placeholder with fixed aspect ratio */}
      <div className={`relative overflow-hidden bg-gray-200 ${!loaded ? 'animate-pulse' : ''}`}
           style={{ paddingBottom: '75%' }}>
        {/* Actual image */}
        <img
          src={image.src}
          alt={image.alt}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-500 group-hover:scale-105 ${
            loaded ? 'opacity-100' : 'opacity-0'
          }`}
          loading="lazy"
          onLoad={() => setLoaded(true)}
          onError={() => { setError(true); setLoaded(true); }}
        />
        
        {/* Loading skeleton overlay */}
        {!loaded && !error && (
          <div className="absolute inset-0 bg-gradient-to-br from-gray-200 via-gray-100 to-gray-200 animate-shimmer" />
        )}
        
        {/* Error state */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
            <Image className="w-8 h-8 text-gray-300" />
          </div>
        )}
        
        {/* Hover overlay */}
        <div className="absolute inset-0 bg-brand-dark/0 group-hover:bg-brand-dark/30 transition-colors flex items-end">
          <div className="p-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <p className="text-white text-sm font-medium">{image.showTitle}</p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Video Player Modal
const VideoModal = ({ video, onClose }) => {
  if (!video) return null;
  
  // Extract YouTube video ID
  const getYouTubeId = (url) => {
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    return match ? match[1] : null;
  };
  
  const videoId = getYouTubeId(video.url);
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white z-50"
      >
        <X className="w-8 h-8" />
      </button>
      
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="w-full max-w-5xl aspect-video"
        onClick={(e) => e.stopPropagation()}
      >
        <iframe
          src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
          title={video.title}
          className="w-full h-full"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </motion.div>
    </motion.div>
  );
};

// Gallery Lightbox
const GalleryLightbox = ({ images, currentIndex, onClose, onPrev, onNext }) => {
  if (currentIndex === null) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center"
      onClick={onClose}
    >
      <button
        onClick={onClose}
        className="absolute top-6 right-6 text-white/70 hover:text-white z-50"
      >
        <X className="w-8 h-8" />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        className="absolute left-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
      >
        <ChevronLeft className="w-8 h-8 text-white" />
      </button>
      
      <button
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        className="absolute right-4 top-1/2 -translate-y-1/2 p-3 bg-white/10 rounded-full hover:bg-white/20 transition-colors"
      >
        <ChevronRight className="w-8 h-8 text-white" />
      </button>
      
      <motion.img
        key={currentIndex}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        src={images[currentIndex]?.src}
        alt={images[currentIndex]?.alt}
        className="max-w-[90vw] max-h-[85vh] object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/70 text-sm">
        {currentIndex + 1} / {images.length}
      </div>
    </motion.div>
  );
};

export default function MediaPage() {
  const [shows, setShows] = useState([]);
  const [videos, setVideos] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [totalImages, setTotalImages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [shuffling, setShuffling] = useState(false);
  const [activeVideo, setActiveVideo] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  const fetchGallery = async () => {
    try {
      const res = await fetch(`${API_URL}/api/gallery/random?limit=25`);
      if (res.ok) {
        const data = await res.json();
        setGallery(data.images || []);
        setTotalImages(data.total || 0);
      }
    } catch (err) {
      console.error('Gallery fetch error:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const showsData = await getShows();
        setShows(showsData);
        
        const allVideos = showsData
          .filter(show => show.videoUrl)
          .map(show => ({
            id: show.id,
            title: show.title,
            shortTitle: show.shortTitle,
            url: show.videoUrl,
            poster: show.poster
          }));
        setVideos(allVideos);
        
        await fetchGallery();
      } catch (error) {
        console.error('Error fetching media:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const shuffleGallery = async () => {
    setShuffling(true);
    await fetchGallery();
    setTimeout(() => setShuffling(false), 500);
  };

  const openLightbox = (index) => setLightboxIndex(index);
  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex(prev => (prev === 0 ? gallery.length - 1 : prev - 1));
  const nextImage = () => setLightboxIndex(prev => (prev === gallery.length - 1 ? 0 : prev + 1));

  if (loading) {
    return (
      <div className="min-h-screen bg-brand-dark flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-accent-gold border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div data-testid="media-page" className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 bg-brand-dark overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/20 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <p className="font-accent text-accent-gold text-sm tracking-[0.3em] uppercase mb-4">
              Behind The Scenes
            </p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
              Media & Gallery
            </h1>
            <p className="font-body text-white/70 text-xl max-w-2xl mx-auto">
              Watch our show trailers and explore production images from across our touring shows
            </p>
          </motion.div>
        </div>
      </section>

      {/* Video Grid Section */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-3">
              <Film className="w-6 h-6 text-brand-primary" />
              <p className="font-accent text-brand-primary text-sm tracking-[0.3em] uppercase">
                Promo Videos
              </p>
            </div>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-dark">
              Show Trailers
            </h2>
          </div>

          {/* Video Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {videos.map((video, index) => (
              <motion.div
                key={video.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div 
                  className="relative aspect-video bg-brand-dark rounded-xl overflow-hidden cursor-pointer group"
                  onClick={() => setActiveVideo(video)}
                >
                  <img
                    src={`${PUBLIC_URL}${video.poster}`}
                    alt={video.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors" />
                  
                  {/* Play Button */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.95 }}
                      className="w-16 h-16 lg:w-20 lg:h-20 rounded-full bg-white flex items-center justify-center shadow-lg"
                    >
                      <Play className="w-8 h-8 lg:w-10 lg:h-10 text-brand-dark ml-1" fill="currentColor" />
                    </motion.div>
                  </div>
                  
                  {/* Title */}
                  <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                    <h3 className="text-white font-bold text-lg">{video.shortTitle}</h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between mb-12">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <Image className="w-6 h-6 text-brand-primary" />
                <p className="font-accent text-brand-primary text-sm tracking-[0.3em] uppercase">
                  Production Photos
                </p>
              </div>
              <h2 className="font-display text-3xl sm:text-4xl font-bold text-brand-dark">
                Photo Gallery
              </h2>
            </div>
            <button
              onClick={shuffleGallery}
              disabled={shuffling}
              className="flex items-center gap-2 px-5 py-3 bg-brand-primary text-white rounded-full font-semibold hover:bg-brand-dark transition-all disabled:opacity-60"
              data-testid="shuffle-gallery-btn"
            >
              <Shuffle className={`w-5 h-5 transition-transform ${shuffling ? 'animate-spin' : ''}`} />
              {shuffling ? 'Loading...' : 'Shuffle'}
            </button>
          </div>
          
          {/* Call to action */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <p className="text-gray-400 text-sm">Showing {gallery.length} of {totalImages} images</p>
            <p className="text-brand-primary text-sm font-medium flex items-center gap-2">
              <span className="inline-block w-1.5 h-1.5 bg-brand-primary rounded-full animate-pulse"></span>
              Click shuffle to discover more amazing production photos!
            </p>
          </div>

          {/* Masonry Gallery with skeleton loading */}
          <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
            {gallery.map((image, index) => (
              <GalleryImage
                key={`${image.src}-${index}`}
                image={image}
                index={index}
                onClick={() => openLightbox(index)}
              />
            ))}
          </div>

          {gallery.length === 0 && (
            <div className="text-center py-20">
              <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No gallery images available yet</p>
            </div>
          )}
        </div>
      </section>

      {/* Video Modal */}
      <AnimatePresence>
        {activeVideo && (
          <VideoModal video={activeVideo} onClose={() => setActiveVideo(null)} />
        )}
      </AnimatePresence>

      {/* Gallery Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <GalleryLightbox
            images={gallery}
            currentIndex={lightboxIndex}
            onClose={closeLightbox}
            onPrev={prevImage}
            onNext={nextImage}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
